<template>
  <div class="login-screen">
    <div class="login-card">
      <AppAuthBrand :data="brandData" :logoAlt="companyName" subtitle-size="16px" title-color="#151a24"
        subtitle-color="#64748b" gap="10px" />

     
      <!-- Step 1: Send OTP -->
      <div v-if="step === 1">
        <v-form class="form-style">
          <AppTextField v-model="email" type="email"
          autocomplete="off"
          prepend-inner-icon="mdi-email-outline"
            placeholder="Email address" class="auth-input" :error-messages="emailError"
            @update:modelValue="emailError = ''" />
        </v-form>

        <div class="mt-2">
          <AppButtonFlat block @click="handleSendOtp" :loading="isSendingOtp" :disabled="isSendingOtp">
            Send OTP
          </AppButtonFlat>
        </div>
      </div>

      <!-- Step 2: Verify OTP -->
      <div v-else-if="step === 2">
        <v-form class="form-style">
          <AppOTPField
            v-model="otp"
            :length="6"
            type="number"
            :error-message="otpError"
            @update:modelValue="otpError = ''"
           
          />
        </v-form>

        <div class="mt-2">
          <AppButtonFlat block @click="handleVerifyOtp" :loading="isVerifyingOtp" :disabled="isVerifyingOtp">
            Verify OTP
          </AppButtonFlat>
        </div>

        <div class="reset-actions">
          <AppButtonFlat
            color="secondary"
            variant="outlined"
            class="reset-actions__btn"
            :loading="isSendingOtp"
            :disabled="isSendingOtp"
            @click="handleResendOtp"
          >
            Resend OTP
          </AppButtonFlat>
        </div>
      </div>

      <!-- Step 3: Set new password -->
      <div v-else>
        <v-form class="form-style">
          <AppTextField v-model="password" :type="showPassword ? 'text' : 'password'" autocomplete="new-password"
            prepend-inner-icon="mdi-lock-outline" :append-inner-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
              " placeholder="New password" class="auth-input" :error-messages="passwordError"
            @update:modelValue="passwordError = ''" @click:append-inner="showPassword = !showPassword" />

          <AppTextField v-model="confirmPassword" :type="showConfirmPassword ? 'text' : 'password'"
            autocomplete="new-password" prepend-inner-icon="mdi-lock-check-outline" :append-inner-icon="showConfirmPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'
              " placeholder="Confirm new password" class="auth-input" :error-messages="confirmPasswordError"
            @update:modelValue="confirmPasswordError = ''" @click:append-inner="showConfirmPassword = !showConfirmPassword" />
        </v-form>

        <div class="mt-2">
          <AppButtonFlat block @click="handleChangePassword" :loading="isResettingPassword" :disabled="isResettingPassword">
            Change Password
          </AppButtonFlat>
        </div>
      </div>
    </div>
  </div>

</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import AppTextField from "@/shared/components/ui/AppTextField.vue";
import AppOTPField from "@/shared/components/ui/AppOTPField.vue";
import AppAuthBrand from "@/modules/auth/components/AppAuthBrand.vue";
import { resetPasswordOTP, resetPassword, passwordVerifyOTP } from "@/modules/auth/services/auth.api";
import { useSnackbarStore } from "@/shared/stores/snackbar";
import { useAppStore } from '@/shared/stores/app';
import fallbackLogo from '@/assets/s-logo.png';
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";

const step = ref<1 | 2 | 3>(1);
const email = ref("");
const otp = ref("");
const otpError = ref("");
const showPassword = ref(false);
const showConfirmPassword = ref(false);
const password = ref("");
const confirmPassword = ref("");
const isSendingOtp = ref(false);
const isVerifyingOtp = ref(false);
const isResettingPassword = ref(false);
const router = useRouter();
const emailError = ref("");
const passwordError = ref("");
const confirmPasswordError = ref("");
const snackbar = useSnackbarStore();
const appStore = useAppStore();
const companyName = computed(() =>
  (appStore.company?.name || appStore.appName || "GlobalApp").toString().trim(),
);
const logo = computed(() => appStore.company?.logoUrl || fallbackLogo);
const subtitle = computed(() => {
  if (step.value === 1) return "Enter your email to receive a verification code.";
  if (step.value === 2) return `Enter the code sent to ${email.value || "your email"}.`;
  return "Set a new password for your account.";
});
const brandData = computed(() => ({
  logo: logo.value,
  title: companyName.value,
  subtitle: subtitle.value,
}));

function goToStep(nextStep: 1 | 2 | 3) {
  // Clear step-specific errors
  emailError.value = "";
  otpError.value = "";
  passwordError.value = "";
  confirmPasswordError.value = "";

  if (nextStep === 1) {
    otp.value = "";
    password.value = "";
    confirmPassword.value = "";
  }

  if (nextStep === 2) {
    password.value = "";
    confirmPassword.value = "";
  }

  step.value = nextStep;
}

function validateInputs() {
  emailError.value = "";

  if (!email.value.trim()) {
    emailError.value = "Email is required";
  } else if (!/.+@.+\..+/.test(email.value)) {
    emailError.value = "Enter a valid email address";
  }

  return !emailError.value;
}
function validateOtp(length = 6) {
  otpError.value = "";
  const raw = otp.value?.toString().trim() || "";

  if (!raw) {
    otpError.value = "OTP is required";
    return false;
  }

  if (!/^\d+$/.test(raw)) {
    otpError.value = "OTP must be numbers only";
    return false;
  }

  if (raw.length !== length) {
    otpError.value = "Enter a valid OTP";
    return false;
  }

  return true;
}

async function handleVerifyOtp(_value?: unknown) {
  if (isVerifyingOtp.value) return;
  isVerifyingOtp.value = true;
  try {
    // `@finish` emits the OTP string, but `@click="handleVerifyOtp"` passes a MouseEvent.
    if (typeof _value === "string") otp.value = _value;

    if (!validateInputs()) {
      snackbar.error("Enter a valid email first.");
      goToStep(1);
      return;
    }

    if (!validateOtp(6)) return;

    const response = await passwordVerifyOTP({
      email: email.value,
      otp: otp.value,
    });

    snackbar.success(response?.message || "OTP verified. Now set a new password.");
    goToStep(3);
  } catch (error) {
    const errors = error?.response?.data?.errors;
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Unable to verify OTP";

    if (errors) {
      if (errors.email?.length) emailError.value = errors.email[0];
      if (errors.otp?.length) otpError.value = errors.otp[0];
    }

    if (!otpError.value) {
      otpError.value = message;
    }
  } finally {
    isVerifyingOtp.value = false;
  }
}

async function handleChangePassword() {
  otpError.value = "";
  emailError.value = "";
  passwordError.value = "";
  confirmPasswordError.value = "";

  if (!email.value.trim()) {
    emailError.value = "Email is required";
  } else if (!/.+@.+\..+/.test(email.value)) {
    emailError.value = "Enter a valid email address";
  }

  validateOtp(6);

  if (!password.value) {
    passwordError.value = "Password is required";
  } else if (password.value.length < 6) {
    passwordError.value = "Minimum 6 characters";
  }

  if (!confirmPassword.value) {
    confirmPasswordError.value = "Confirm your password";
  } else if (confirmPassword.value !== password.value) {
    confirmPasswordError.value = "Passwords do not match";
  }

  if (emailError.value || otpError.value || passwordError.value || confirmPasswordError.value) {
    return;
  }

  try {
    isResettingPassword.value = true;
    const response = await resetPassword({
      email: email.value,
      password: password.value,
      password_confirmation: confirmPassword.value,
    });

    snackbar.success(response?.message || "Password changed successfully.");
    router.push({ name: "login.page" });
  } catch (error) {
    const errors = error?.response?.data?.errors;
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Unable to reset password";

    if (errors) {
      if (errors.email?.length) emailError.value = errors.email[0];
      if (errors.otp?.length) otpError.value = errors.otp[0];
      if (errors.password?.length) passwordError.value = errors.password[0];
      if (errors.password_confirmation?.length) {
        confirmPasswordError.value = errors.password_confirmation[0];
      }
    }

    if (!emailError.value && !otpError.value && !passwordError.value && !confirmPasswordError.value) {
      snackbar.error(message);
    }
  } finally {
    isResettingPassword.value = false;
  }
}

async function handleSendOtp() {
  if (!validateInputs()) return;
  try {
    isSendingOtp.value = true;
    emailError.value = "";
    otpError.value = "";
    const response = await resetPasswordOTP({ email: email.value });
    snackbar.success(response.message || "OTP sent to your email.");
    otp.value = "";
    otpError.value = "";
    goToStep(2);
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      "Unable to send OTP";
    emailError.value = message;
  } finally {
    isSendingOtp.value = false;
  }
}

async function handleResendOtp() {
  if (step.value !== 2) return;
  await handleSendOtp();
  step.value = 2;
}
</script>

<style scoped>
.login-screen {
  height: calc(100vh - 250px);
  display: flex;
  align-items: center;
}

.login-card {
  width: 100%;
  height: calc(100vh - 170px);
  border-radius: 18px;
  padding: 26px 22px 30px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-style {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.reset-steps {
  display: flex;
  align-items: center;
  gap: 10px;
}

.reset-steps__item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
}

.reset-steps__item.active {
  color: #0f172a;
}

.reset-steps__bubble {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-weight: 700;
}

.reset-steps__item.active .reset-steps__bubble {
  border-color: var(--color-theme, #e8612f);
}

.reset-steps__item.done .reset-steps__bubble {
  background: var(--color-theme, #e8612f);
  border-color: var(--color-theme, #e8612f);
  color: #fff;
}

.reset-steps__label {
  font-size: 13px;
  font-weight: 600;
}

.reset-steps__line {
  flex: 1;
  height: 2px;
  background: #e2e8f0;
  border-radius: 999px;
}

.reset-steps__line.done {
  background: var(--color-theme, #e8612f);
}

.reset-actions {
  margin-top: 10px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

.reset-actions__btn {
  height: 44px !important;
  font-size: 14px;
}
</style>
